import React from "react";
import { useConsultantApplication } from "@/hooks/useConsultantApplication";
import { useSignupConsents } from "@/hooks/useSignupConsents";
import { SignupProgress } from "@/components/consultant-signup/SignupProgress";
import { BasicInfoStep } from "@/components/consultant-signup/BasicInfoStep";
import { CompanyInfoStep } from "@/components/consultant-signup/CompanyInfoStep";
import { ProfileSkillsStep } from "@/components/consultant-signup/ProfileSkillsStep";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router";
import type { ApplicationFormData, ApplicationSubmitRequest } from "@/types/consultant-application";
import { resolveRateCurrency } from "@/lib/rateCurrency";

// Public privacy policy (#863). Opened in a new tab so the wizard keeps its state.
const PRIVACY_POLICY_URL = "https://bondit.dk/en/privacy-policy";
// Same address the success screen already shows.
const SUPPORT_EMAIL = "support@bondit.dk";

const PublicConsultantSignupPage: React.FC = () => {
  const {
    state,
    updateFormData,
    startApplication,
    verifyEmail,
    resendVerificationCode,
    nextStep,
    previousStep,
    searchSkills,
    submitApplication,
  } = useConsultantApplication();

  const currentYear = new Date().getFullYear();

  // Only fetched once the applicant reaches the final step that shows them.
  const consents = useSignupConsents(!state.isRestoringSession && state.currentStep === 3);

  // Show success page after submission
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleSubmit = async (acknowledgements: ApplicationSubmitRequest) => {
    try {
      await submitApplication(acknowledgements);
      setIsSubmitted(true);
    } catch (error) {
      // Error is handled by the hook
      console.error("Submission failed:", error);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Application Submitted!</CardTitle>
            <CardDescription className="text-base">
              Thank you for applying to join our consultant network.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-left space-y-3 text-sm">
              <p>
                <strong>What's next?</strong>
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Our team will review your application within 2-3 business days</li>
                <li>• You'll receive an email with the review results</li>
                <li>
                  • If approved, you'll get login credentials and access to the consultant portal
                </li>
                <li>• You can then browse and apply for available assignments</li>
              </ul>
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-3">
                Questions? Contact us at {SUPPORT_EMAIL}
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return to Home
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/bondit_collegues_table.jpeg)",
        }}
      >
        {/* Dark overlay for better contrast */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
          <div className="container mx-auto px-4 py-2 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center">
                <img
                  src="/bondit-logo-trans-bg.png"
                  alt="BondIT Logo"
                  className="h-8 sm:h-12 w-auto filter drop-shadow-lg sm:mb-2"
                />
                <p className="hidden sm:block text-sm text-white/90 drop-shadow text-center">
                  Join our network of skilled consultants
                </p>
              </div>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-white/10 border-white/20"
              >
                <Link to="/login">Already have an account?</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-4 sm:py-8">
          {/* Progress Indicator */}
          <SignupProgress
            currentStep={state.currentStep}
            totalSteps={3}
            emailVerified={state.emailVerified}
            className="mb-4 sm:mb-8"
          />

          {/* Step Content - Wrapped in semi-transparent cards. The cards are
              always white on the photo backdrop, so the subtree is pinned to
              the light palette — otherwise a dark OS theme paints the fields
              inside with dark-surface tokens (#890). */}
          <div className="light max-w-2xl mx-auto">
            {/* The step components snapshot `formData` into their form
                defaults on mount, so nothing may mount until a stored session
                has finished restoring — otherwise the restored name is held in
                wizard state while the field the applicant sees is blank
                (#873). */}
            {state.isRestoringSession && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20 p-10 flex items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Restoring your application…</span>
              </div>
            )}

            {!state.isRestoringSession && state.currentStep === 1 && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20">
                <BasicInfoStep
                  formData={{
                    fullName: state.formData.fullName,
                    email: state.formData.email,
                    phone: state.formData.phone,
                  }}
                  emailVerified={state.emailVerified}
                  isLoading={state.isLoading}
                  error={state.errors.application || state.errors.verification}
                  hasActiveSession={state.sessionId !== null}
                  verificationEmail={state.verificationEmail}
                  codeExpiresInMinutes={state.codeExpiresInMinutes}
                  onFormChange={data => updateFormData(data)}
                  onStartApplication={startApplication}
                  onVerifyEmail={verifyEmail}
                  onResendCode={resendVerificationCode}
                  onNext={nextStep}
                  onBack={previousStep}
                />
              </div>
            )}

            {!state.isRestoringSession && state.currentStep === 2 && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20">
                <CompanyInfoStep
                  formData={{
                    companyName: state.formData.companyName,
                    billingEmail: state.formData.billingEmail,
                    cvrNumber: state.formData.cvrNumber,
                    address: state.formData.address,
                    postalCode: state.formData.postalCode,
                    city: state.formData.city,
                    country: state.formData.country,
                    website: state.formData.website,
                  }}
                  applicantEmail={state.formData.email}
                  isLoading={state.isLoading}
                  error={state.errors.company}
                  onFormChange={data => updateFormData(data)}
                  onNext={nextStep}
                  onBack={previousStep}
                />
              </div>
            )}

            {!state.isRestoringSession && state.currentStep === 3 && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20">
                <ProfileSkillsStep
                  formData={{
                    bio: state.formData.bio,
                    linkedinUrl: state.formData.linkedinUrl,
                    yearsExperience: state.formData.yearsExperience,
                    hourlyRate: state.formData.hourlyRate,
                    hourlyRateCurrency: resolveRateCurrency(
                      state.formData.hourlyRateCurrency,
                      state.formData.country
                    ),
                    interestedInEmployment: state.formData.interestedInEmployment,
                    selectedSkills: state.formData.selectedSkills,
                    cvUrl: state.formData.cvUrl,
                  }}
                  sessionId={state.sessionId}
                  isLoading={state.isLoading}
                  error={state.errors.profile || state.errors.submit}
                  onFormChange={data => updateFormData(data as Partial<ApplicationFormData>)}
                  onSearchSkills={searchSkills}
                  consentDocuments={consents.documents}
                  consentsLoading={consents.isLoading}
                  consentsError={consents.error}
                  onRetryConsents={consents.reload}
                  onNext={handleSubmit}
                  onBack={previousStep}
                />
              </div>
            )}
          </div>

          {/* Progress indicator - no auto-save needed */}
        </div>

        {/* Footer */}
        <div className="bg-white/10 backdrop-blur-sm border-t border-white/20 py-6 mt-16">
          <div className="container mx-auto px-4">
            <div className="text-center text-sm text-white/80">
              <p>© {currentYear} BondIT Denmark ApS. All rights reserved.</p>
              {/* Terms of Service was a dead button; there are no ToS yet (#863). */}
              <div className="flex justify-center gap-4 mt-2">
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block py-1.5 -my-1.5 hover:text-white transition-colors text-white/70"
                >
                  Privacy Policy
                </a>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-block py-1.5 -my-1.5 hover:text-white transition-colors text-white/70"
                >
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicConsultantSignupPage;
